import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ESLint's react/require-default-props rule does not recognise forwardRef-wrapped components
// as functional components, so it fires even though defaults are in the destructuring.
/* eslint-disable react/require-default-props */
import { useState, useEffect, useRef, forwardRef, useImperativeHandle, } from 'react';
import { Dropdown, Input, Tooltip, } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import clsx from 'clsx';
import { txt } from 'libs/i18n';
import { useSpeakerSuggestions } from 'screens/DashboardScreen/pages/EditorPage/DocumentEditor/SpeakerEditorModal/useSpeakerSuggestions';
import SpeakerAvatar from 'components/SpeakerAvatarField/SpeakerAvatar';
import { SpeakerTreePanel } from './SpeakerTreePanel';
import { SpeakersTree } from './speakers-tree';
import styles from './style.module.less';
import { getFullName } from '../speaker-utils';
const EMPTY_PINNED_SPEAKERS = [];
export const SpeakerTreeSelect = forwardRef(({ autoFocus = false, allowButtonActions = true, helperText, pinnedSpeakers = EMPTY_PINNED_SPEAKERS, value, onChange, onAction, onAvatarChange, }, ref) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchString, setSearchString] = useState('');
    const [tree, setTree] = useState(() => SpeakersTree.empty());
    const inputRef = useRef(null);
    const triggerRef = useRef(null);
    useImperativeHandle(ref, () => ({
        focus: () => { var _a; (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); },
    }), []);
    useEffect(() => {
        var _a;
        if (autoFocus === false) {
            return;
        }
        // Programmatic focus so the onFocus handler opens the dropdown — at that point Ant Design's
        // outside-click listener hasn't been registered yet, so the dropdown won't be immediately
        // dismissed by the click event that opened the parent modal.
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    useEffect(() => {
        var _a;
        if (isDropdownOpen) {
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [isDropdownOpen]);
    const [pushQuery, speakers] = useSpeakerSuggestions();
    useEffect(() => {
        if (speakers === 'loading') {
            setTree(SpeakersTree.empty());
        }
        else {
            setTree(SpeakersTree.fromSpeakers(speakers, pinnedSpeakers, true));
        }
    }, [speakers, pinnedSpeakers]);
    const handleOpenChange = (open) => {
        setIsDropdownOpen(open);
    };
    const handleQueryChange = (event) => {
        const query = event.target.value;
        setSearchString(query);
        pushQuery(query, 'combined');
        if (query === '') {
            setIsDropdownOpen(false);
        }
    };
    const handleAction = (action) => {
        var _a, _b, _c;
        // addSpeaker only needs the search text — allow it through even while speakers are loading
        // so that pressing Enter is never silently dropped when the suggestions query is in flight.
        if (speakers === 'loading' && action.kind !== 'addSpeaker') {
            return;
        }
        if (action.kind !== 'selectSpeaker') {
            setIsDropdownOpen(false);
            setSearchString('');
            if (action.kind === 'addSpeaker' && searchString !== '') {
                onAction === null || onAction === void 0 ? void 0 : onAction(Object.assign(Object.assign({}, action), { searchText: searchString }));
            }
            else {
                onAction === null || onAction === void 0 ? void 0 : onAction(action);
            }
            return;
        }
        const { selection } = action;
        const { speaker, roleIndex } = selection;
        const role = roleIndex === 'none' ? null : (_c = (_b = (_a = speaker.children) === null || _a === void 0 ? void 0 : _a[roleIndex]) === null || _b === void 0 ? void 0 : _b.title) !== null && _c !== void 0 ? _c : null;
        const existingDocSpeaker = speaker.savedInDb && speakers !== 'loading'
            ? speakers.find((node) => !node.savedInDb && node.dbid === speaker.dbid && node.role === role)
            : undefined;
        const resolvedSpeaker = existingDocSpeaker !== null && existingDocSpeaker !== void 0 ? existingDocSpeaker : speaker;
        setIsDropdownOpen(false);
        setSearchString('');
        onChange === null || onChange === void 0 ? void 0 : onChange(Object.assign(Object.assign({}, selection), { speaker: resolvedSpeaker }));
    };
    const handleKeyDown = (e) => {
        const isTyping = e.key.length === 1
            && e.ctrlKey === false && e.metaKey === false && e.altKey === false;
        if (!isDropdownOpen && isTyping) {
            setIsDropdownOpen(true);
            return;
        }
        if (e.key === 'ArrowDown' && e.shiftKey === false) {
            e.preventDefault();
            setTree(tree.moveFocusDown());
        }
        else if (e.key === 'ArrowUp' && e.shiftKey === false) {
            e.preventDefault();
            setTree(tree.moveFocusUp());
        }
        else if (e.key === 'ArrowDown' && e.shiftKey === true) {
            e.preventDefault();
            setTree(tree.expandFocused(allowButtonActions));
        }
        else if (e.key === 'ArrowUp' && e.shiftKey === true) {
            e.preventDefault();
            setTree(tree.collapseFocused());
        }
        else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            handleAction(tree.getAction());
        }
    };
    const getDisplayText = () => {
        var _a;
        if (value === undefined) {
            return '';
        }
        const nameSource = value.speaker.savedInDb
            ? ((_a = value.speaker.defaultNameVariant) !== null && _a !== void 0 ? _a : value.speaker)
            : value.speaker;
        const name = getFullName(nameSource.firstName, nameSource.lastName);
        return value.speaker.role !== null ? `${name} (${value.speaker.role})` : name;
    };
    const getSelectedSuffix = () => {
        if (isDropdownOpen || value === undefined) {
            return undefined;
        }
        return (_jsxs("span", { className: styles.selectedSuffix, onClick: (e) => e.stopPropagation(), onMouseDown: (e) => e.stopPropagation(), role: "presentation", children: [value.speaker.savedInDb && (_jsx(Tooltip, { title: txt('speakerInDatabase'), children: _jsx(TeamOutlined, {}) })), _jsx(SpeakerAvatar, { value: value.speaker.imageBase64, editable: value.speaker.savedInDb === true && onAvatarChange !== undefined, onChange: onAvatarChange })] }));
    };
    return (_jsxs("div", { id: "speaker-tree-select", children: [helperText !== undefined && (_jsx("div", { className: styles.helperText, children: helperText })), _jsx(Dropdown, { open: isDropdownOpen, onOpenChange: handleOpenChange, 
                // trigger={['click']} is required so Ant Design registers its document-level mousedown
                // listener, which is what closes the dropdown when the user clicks outside. Without it,
                // outside clicks do nothing. We do NOT want the click-to-toggle behaviour that comes with
                // it, though: clicking the input fires onFocus first (which opens the dropdown), and by
                // the time the click bubbles to this trigger div Ant Design sees the dropdown is already
                // open and calls onOpenChange(false), closing it immediately. The onClick stopPropagation
                // on the Input below breaks that bubble so the toggle never fires.
                trigger: ['click'], 
                // eslint-disable-next-line react/no-unstable-nested-components -- Ant Design render prop
                dropdownRender: () => {
                    var _a;
                    return (_jsx(SpeakerTreePanel, { tree: tree, width: (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth, allowButtonActions: allowButtonActions, onAction: handleAction, onToggleExpand: (i) => setTree(tree.toggleExpand(i)) }));
                }, children: _jsx("div", { ref: triggerRef, className: clsx(styles.trigger, { [styles.open]: isDropdownOpen }), children: _jsx(Input, { ref: inputRef, className: styles.searchInput, value: !isDropdownOpen && searchString === '' ? getDisplayText() : searchString, placeholder: isDropdownOpen ? txt('findSpeaker') : txt('selectSpeaker'), suffix: getSelectedSuffix(), onClick: (e) => e.stopPropagation(), onChange: handleQueryChange, onKeyDown: handleKeyDown }) }) })] }));
});
